// src/pages/subscription/SubscriptionDetailPage.tsx
// Auto-generated by corebuilder. Edit freely.

import { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useSubscription, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Alert,
  ErrorAlert,
} from '@react/components';

/**
 * Subscription detail page.
 */
export const SubscriptionDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useSubscription();
  const { t } = useI18n();
  const item = store.currentItem;

  useEffect(() => {
    if (id) {
      store.GetSubscription(id);
    }
    return () => {
      store.clearCurrent();
    };
  }, [id]);
  const handleDelete = async () => {
    if (!id || !confirm(t('subscription.deleteConfirm'))) return;
    try {
      await store.DeleteSubscription(id);
      navigate('/subscriptions');
    } catch {
      // Error is handled by store
    }
  };

  if (store.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (store.error) {
    return (
      <>
        <ErrorAlert error={store.error} />
        <Link to="/subscriptions" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </>
    );
  }
  if (!item) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to="/subscriptions" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">
              {t('subscription.list')}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{t('subscription.details')}</span>
          </nav>
          <h1 className="text-2xl font-bold text-foreground">{t('subscription.details')}</h1>
        </div>
        <div className="flex gap-2">
          <Link to={`/subscriptions/${id}/edit`}>
            <Button variant="secondary">{t('common.edit')}</Button>
          </Link>
          <Button variant="danger" onClick={handleDelete}>
            {t('common.delete')}
          </Button>
        </div>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>{t('subscription.details')}</CardTitle>
          <CardDescription>{t('subscription.title')}</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Entity</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.entity ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Plan</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.plan ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Tenant</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.tenant ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Billing Account</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.billing_account ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Latest Invoice</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.latest_invoice ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Owner</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.owner ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Status</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.status ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Quantity</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.quantity ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Current Price</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.current_price ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Currency</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.currency ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Billing Cycle</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.billing_cycle ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Payment Method Id</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.payment_method_id ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Latest Invoice Id</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.latest_invoice_id ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Current Period Start</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.current_period_start ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Current Period End</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.current_period_end ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Trial Start</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.trial_start ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Trial End</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.trial_end ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Started At</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.started_at ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Ends At</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.ends_at ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Cancelled At</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.cancelled_at ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Cancellation Reason</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.cancellation_reason ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Cancellation Comment</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.cancellation_comment ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Renewal Settings</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.renewal_settings ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Features</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.features ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Usage</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.usage ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Applied Discounts</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.applied_discounts ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Items</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.items ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Resources</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.resources ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Aspects</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.aspects ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Metadata</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.metadata ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Created At</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.created_at ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Updated At</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.updated_at ?? '')}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
});

export default SubscriptionDetailPage;
